// Marketing attribution: who sent this reader, and the id that ties their ad
// click to whatever they buy.
//
// The marketing site stamps every outbound funnel link with `?visitor-id`
// (monorepo `src/lib/lead-id.ts`); until now this app dropped it, so the
// funnel could show that *someone* subscribed but never which campaign paid
// for them. This module closes that gap.
//
// Three deliberate splits:
//
//  1. **Identity vs. campaign.** `leadId` is a correlation field on EVERY
//     event (see `ContextFields`); campaign detail rides in `data` on a single
//     `acquisition.landed` event. Attribution is then an APL join on `leadId`,
//     and the Axiom field budget absorbs one slot rather than eight.
//
//  2. **No attribution model in the EVENT STREAM.** Every landing that carries
//     campaign params emits its own event, so the stream holds the complete
//     touch history and first-touch / last-touch / multi-touch stay query-time
//     decisions. A reader in a seven-impression funnel has seven rows, not one
//     guess.
//
//     The one stored click id (below) is not an exception to that: it is a
//     carrier, not a model. Nothing reads it to decide who gets credit — it
//     exists so the click id still EXISTS at checkout, weeks later, to be sent
//     back to the platform that needs it.
//
//  3. **Descriptive vs. identifying.** A UTM tag NAMES a campaign; a click id
//     IDENTIFIES one person's click on one ad. The tags are logged, the click
//     ids are only ever stored — a `gclid` in Axiom joins to `leadId` joins to
//     `userId`, which reconstructs a per-reader ad-click history inside a
//     dataset never scoped to hold advertising identifiers. `Landing` keeps
//     the two halves in separate fields so re-merging them has to be a
//     deliberate act rather than a one-word edit to a params array.
//
// Nothing here reads a third-party cookie or loads a pixel: the ids live in our
// own first-party storage, and conversions are uploaded server-side from Axiom.
//
// That storage is now TWO places, and the second one is not redundancy. WebKit's
// ITP deletes all script-writable storage — `localStorage` included — for a site
// the visitor reached by a cross-site navigation to a link-decorated URL, after
// a stretch of days without interaction. `google.com` → `retells…/?gclid=…` is
// exactly that shape, so `localStorage` alone lost the click id for precisely
// the traffic it exists to serve, well inside the 89-day upload window. A
// server-set cookie is the durable half (`src/worker/visitor.ts` explains why
// the creation method, not the readability, is what WebKit caps), so every read
// below is a cascade across both and every page load refreshes the cookie.

import {
  getPref,
  removePref,
  setPref,
  PREF_KEYS,
  type StoredClickId,
} from "@/lib/prefs";
import { AUTH_BROKER_ORIGIN } from "@/lib/auth";
import { AUTH_HOSTS } from "@worker/config";
import {
  CLICK_COOKIE,
  VISITOR_COOKIE,
  VISITOR_ENDPOINT,
  VISITOR_MAX_AGE_SECONDS,
  decodeClickCookie,
  serializeVisitorCookie,
} from "@worker/visitor";
import { createClientLogger } from "@/logging";

/** Query param the marketing site hands the lead id over in. */
export const LEAD_PARAM = "visitor-id";

/**
 * Tags we set ourselves on our own outbound links. DESCRIPTIVE — they name a
 * campaign, not a person — which is why these are the half that gets logged.
 */
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Click ids an ad platform appends to the destination URL, MOST PREFERRED
 * FIRST — this order is what `pickClickId` resolves a landing carrying more
 * than one by. Google's three lead because it is the only platform we
 * currently upload offline conversions to.
 *
 * IDENTIFIERS, never logged (see split 3 above): these are captured into
 * storage for checkout to send back at purchase time, and nothing else.
 *
 * `gbraid`/`wbraid` are Google's iOS answer to ATT: with tracking denied there
 * is no `gclid` at all, and the landing arrives carrying one of these instead.
 * Dropping them made every iOS ad group report zero conversions while it was
 * actually selling — the sale was skipped as `no_click_id`, and the ad group
 * was bid down for it. Google's Data Manager import accepts all three, and
 * accepts exactly ONE per event, so the order below is also the tie-break:
 * `gclid` is the most specific (one click), then `gbraid` (app-to-web), then
 * `wbraid` (web-to-app).
 */
export const CLICK_ID_PARAMS = [
  "gclid", // Google Ads
  "gbraid", // Google Ads — iOS app-to-web, no gclid available
  "wbraid", // Google Ads — iOS web-to-app, no gclid available
  "msclkid", // Microsoft Advertising
  "fbclid", // Meta
  "ttclid", // TikTok
  "rdt_cid", // Reddit
  "epik", // Pinterest
  "li_fat_id", // LinkedIn
] as const;

/**
 * Every param we take off the landing URL — and therefore every param
 * `stripCapturedParams` must remove from the address bar.
 *
 * THIS UNION IS FOR STRIPPING ONLY. Nothing logged is derived from it: the
 * logged payload is built from `Landing.tags`, which the click ids never enter
 * (split 3). It used to be the log payload too, which is how a `gclid` reached
 * Axiom on every ad landing while three other places in the codebase said it
 * never could.
 *
 * An allowlist rather than "every param", because policy §11 forbids logging
 * raw request URLs — they carry auth codes and other tokens, and a blanket
 * copy would eventually sweep one into the dataset.
 */
export const CAPTURED_PARAMS = [...UTM_PARAMS, ...CLICK_ID_PARAMS] as const;

/**
 * Cap on a captured UTM tag. Campaign names run short; this leaves headroom
 * while keeping a hand-crafted URL from filling the worker's per-event `data`
 * budget on its own. Click ids are NOT truncated — see `CLICK_ID_PATTERN`.
 */
const MAX_VALUE_LENGTH = 256;

/** Lead ids are opaque ids, matched against the worker's `SAFE_ID` shape. */
const LEAD_ID_PATTERN = /^[a-zA-Z0-9_-]{1,100}$/;

/**
 * Click-id shape. MUST stay character-for-character identical to
 * `CLICK_ID_PATTERN` in `convex/model/attribution.ts` — that is the check a
 * value must pass to be stored on an order, and anything this one lets through
 * that the other rejects is a value we keep in `localStorage` forever, re-send
 * at every checkout, and silently lose server-side.
 *
 * Looser than the lead id in the two ways the platforms force: `.` is allowed
 * (Meta's `fbclid` is dotted) and the cap is 256 (a `gclid` runs 100–200).
 *
 * The cap is enforced by REJECTING, never by truncating. A 300-character value
 * sliced to 256 passes both this and the server's identical check, and is then
 * uploaded to Google as a garbage id that matches no click — worse than having
 * captured nothing, because the sale is stamped as reported.
 */
export const CLICK_ID_PATTERN = /^[a-zA-Z0-9_.-]{1,256}$/;

/**
 * Oldest stored click we will still send at checkout. Mirrors
 * `MAX_CLICK_AGE_MS` in `convex/model/attribution.ts`; the upload job's own
 * deadline (`UPLOAD_WINDOW_MS`, 89 days) sits just inside it.
 */
export const MAX_CLICK_AGE_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Hosts that are US. A hop from any of these is a redirect we sent the reader
 * on ourselves: checkout bounces every purchase back here and sign-in
 * round-trips the broker, each returning as a full page load with that host in
 * `document.referrer`. Counted as referrals they file a fake acquisition touch
 * milliseconds before every `checkout.entitlement_granted` — so the last touch
 * before EVERY conversion is our own payment host, and referrer counts inflate
 * by one row per purchase and per login.
 *
 * `AUTH_BROKER_ORIGIN` and `AUTH_HOSTS` are the definitions those flows
 * actually redirect through, borrowed rather than re-typed. The checkout host
 * has no client-side definition to borrow — it lives in
 * `convex/lib/checkoutClient.ts`, which is server-only — so it is named here.
 */
const OWN_HOSTS: readonly string[] = [
  new URL(AUTH_BROKER_ORIGIN).hostname,
  "checkout.sara.ai",
  ...AUTH_HOSTS,
];

/** The loggable half of a landing: UTM tags plus the referring hostname. */
export type CampaignSnapshot = Record<string, string>;

/** The click ids a landing carried, keyed by the param each arrived under. */
export type ClickIdSnapshot = Record<string, string>;

/**
 * What a landing contributes, split at the line that governs where each half
 * may travel: `tags` are logged, `clickIds` are stored and never logged.
 */
export interface Landing {
  tags: CampaignSnapshot;
  clickIds: ClickIdSnapshot;
}

/**
 * The first NON-BLANK occurrence of a param.
 *
 * `URLSearchParams.get` returns the first occurrence full stop, which loses
 * the click id on the one URL shape Google actually produces: a destination
 * template that already carries an empty `gclid=` plus auto-tagging's appended
 * real one resolves to `""`, and the click records as untagged.
 */
function firstValue(params: URLSearchParams, key: string): string | null {
  for (const raw of params.getAll(key)) {
    const value = raw.trim();
    if (value) return value;
  }
  return null;
}

/**
 * Split a landing's query string and referrer into what may be logged and what
 * may only be stored. Pure — `search`, `referrer` and `hostname` are all
 * passed in, so nothing here reads `window` and the same-origin suppression
 * below is reachable under a test runner with no DOM.
 *
 * Returns `null` when the landing carries no attribution signal at all, which
 * is what keeps a direct visit or an internal navigation from emitting an
 * empty `acquisition.landed`.
 *
 * Only the referrer's hostname is kept: the full referring URL is a raw URL
 * (policy §11) and its query string is somebody else's business, while the
 * hostname is the whole of what organic attribution needs — that a reader
 * arrived from a podcast's show notes rather than an ad.
 */
export function parseLanding(
  search: string,
  referrer: string,
  hostname: string,
): Landing | null {
  const params = new URLSearchParams(search);
  const tags: CampaignSnapshot = {};
  const clickIds: ClickIdSnapshot = {};

  for (const key of UTM_PARAMS) {
    // Descriptive, so an absurd one is truncated rather than dropped: a
    // mangled campaign name still names the campaign.
    const value = firstValue(params, key);
    if (value) tags[key] = value.slice(0, MAX_VALUE_LENGTH);
  }

  for (const key of CLICK_ID_PARAMS) {
    // Functional, so it is the exact string the platform will match or it is
    // nothing. A value the server would refuse is dropped HERE, at capture,
    // rather than stored forever and re-sent at every checkout to be dropped
    // there every time.
    const value = firstValue(params, key);
    if (value && CLICK_ID_PATTERN.test(value)) clickIds[key] = value;
  }

  const host = referrerHost(referrer, hostname);
  if (host) tags.referrer_host = host;

  const empty =
    Object.keys(tags).length === 0 && Object.keys(clickIds).length === 0;
  return empty ? null : { tags, clickIds };
}

/**
 * The hostname of a referring URL, or `null` for a direct arrival, an
 * unparseable value, or a hop from one of our own properties (`OWN_HOSTS`) or
 * this same origin — none of which say anything about acquisition.
 *
 * `document.referrer` is untrusted external input — it is whatever the
 * previous page's policy chose to send — so `URL` parsing goes in a `try`.
 */
function referrerHost(referrer: string, hostname: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname;
    if (!host) return null;
    if (host === hostname || OWN_HOSTS.includes(host)) return null;
    return host.slice(0, MAX_VALUE_LENGTH);
  } catch {
    return null;
  }
}

/** True when a value is shaped like a lead id we would accept or store. */
export function isValidLeadId(value: string): boolean {
  return LEAD_ID_PATTERN.test(value);
}

/**
 * The one click id worth keeping out of a landing, or `null` when it carried
 * none. Pure — `now` is passed in.
 *
 * A landing can carry more than one (a Meta ad linking to a page we also tag
 * for Google, a shared URL that accumulated both), and only one can be stored,
 * so `CLICK_ID_PARAMS` order decides: the platform we actually upload
 * conversions to wins.
 */
export function pickClickId(
  clickIds: ClickIdSnapshot,
  now: number,
): StoredClickId | null {
  for (const source of CLICK_ID_PARAMS) {
    const value = clickIds[source];
    if (value) return { source, value, at: now };
  }
  return null;
}

/**
 * The `data` payload for `acquisition.landed`: the descriptive half of the
 * landing, plus WHICH platform's click id was present.
 *
 * PRESENCE, NEVER THE VALUE. Knowing a landing was `gclid`-tagged is what
 * campaign analysis needs to tell paid traffic from the rest; the id itself
 * buys the query nothing and costs the dataset an advertising identifier it
 * was never scoped to hold. Pure, so the rule is testable without a browser.
 */
export function landedPayload(
  landing: Landing,
  click: StoredClickId | null,
): Record<string, string> {
  return click
    ? { ...landing.tags, click_source: click.source }
    : { ...landing.tags };
}

/**
 * What checkout needs to send to the server so a purchase can be reported back
 * to the ad platform that paid for it. Read at click-the-buy-button time, not
 * at landing time — the two can be weeks apart.
 */
export interface CheckoutAttribution {
  leadId?: string;
  clickId?: string;
  clickSource?: string;
  clickAt?: number;
}

/**
 * The stored attribution, shaped for the `createSession` /
 * `createSubscriptionSession` args. Absent keys are omitted rather than sent
 * as `undefined`, because Convex has no `undefined` value.
 *
 * `leadId` is passed IN — it is the id this session is actually logging under
 * (`LoggerProvider`'s context), not a fresh read of storage. With storage
 * blocked (a restricted webview, Safari with site data disabled) `setPref`
 * no-ops, so `recordLanding` mints an id that rides on every Axiom event while
 * the stored key stays empty; re-reading it here returned nothing and left the
 * order row unjoinable for exactly the readers whose storage is restricted.
 *
 * Everything here is advisory: the server re-validates it
 * (`convex/model/attribution.ts`) and drops what it does not recognize, so a
 * reader who edits their own `localStorage` corrupts nothing but their own
 * attribution.
 */
export function checkoutAttribution(
  leadId: string | undefined,
  now: number = Date.now(),
): CheckoutAttribution {
  const attribution: CheckoutAttribution = {};
  if (leadId) attribution.leadId = leadId;

  const click = resolveStoredClick(now);
  if (!click) return attribution;

  attribution.clickId = click.value;
  attribution.clickSource = click.source;
  attribution.clickAt = click.at;
  return attribution;
}

/**
 * One cookie's value from `document.cookie`, or `undefined` when it is absent
 * or unreadable.
 *
 * Untrusted and unreliable input, treated like `getPref` treats a stored blob:
 * `document.cookie` can be empty, can be blocked outright, and throws
 * `SecurityError` in a sandboxed iframe with an opaque origin. It is also a
 * flat string holding every cookie on the host, so the name match has to be
 * anchored — a substring search would let `not-retells-visitor` answer for
 * `retells-visitor`. Nothing here decodes: everything we write is drawn from
 * alphabets with no cookie metacharacter in them (`src/worker/visitor.ts`), so
 * a value needing `decodeURIComponent` is by definition not ours and is left to
 * fail its shape check below.
 */
function readCookie(name: string): string | undefined {
  try {
    if (typeof document === "undefined") return undefined;
    const jar = document.cookie;
    if (!jar) return undefined;
    for (const part of jar.split(";")) {
      const eq = part.indexOf("=");
      if (eq < 0) continue;
      if (part.slice(0, eq).trim() !== name) continue;
      const value = part.slice(eq + 1).trim();
      return value || undefined;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolve the lead id for this browser: inbound → cookie → `localStorage` →
 * mint.
 *
 * Inbound wins over everything deliberately: the marketing site put that exact
 * id on the link the reader just clicked, so it is the id the ad platform's
 * click will be reconciled against. Keeping a previously stored id instead
 * would split one person across two ids and break the join at the only moment
 * it matters.
 *
 * The cookie then outranks `localStorage` because it is the copy that SURVIVES
 * — under ITP the storage read below returns nothing on exactly the ad traffic
 * this module exists for, and a miss there means minting a second id for a
 * reader we already know. Whichever tier answers is written back down to
 * `localStorage`, so the two halves reconverge on one id rather than drifting
 * apart with each visit; the cookie is refreshed in the other direction by
 * `syncVisitorCookie`.
 */
export function resolveLeadId(inbound: string | null): string {
  if (inbound && isValidLeadId(inbound)) return adoptLeadId(inbound);

  const fromCookie = readCookie(VISITOR_COOKIE);
  if (fromCookie && isValidLeadId(fromCookie)) return adoptLeadId(fromCookie);

  const stored = getPref(PREF_KEYS.leadId, "");
  if (stored) return stored;

  return adoptLeadId(crypto.randomUUID());
}

/** Persist a resolved lead id, skipping the write when it is already stored. */
function adoptLeadId(leadId: string): string {
  if (leadId !== getPref(PREF_KEYS.leadId, "")) {
    setPref(PREF_KEYS.leadId, leadId);
  }
  mirrorVisitorCookie(leadId);
  return leadId;
}

/**
 * Best-effort script-side write of the visitor cookie, so BOTH tiers of the
 * cascade hold the adopted id immediately.
 *
 * The gap this closes: the cookie outranks `localStorage` in `resolveLeadId`,
 * and the durable server-set refresh (`syncVisitorCookie`) is fire-and-forget.
 * A landing that adopts an inbound `?visitor-id=A` over a stale cookie `B`,
 * whose POST then never lands (offline, an ad-blocker filtering `/api/…`),
 * would otherwise read `B` back out of the cookie on the next page load and
 * overwrite `A` — splitting the marketing join at the only moment it matters.
 *
 * A script-set cookie lives on WebKit's shorter leash, which is fine: it only
 * has to bridge until a `syncVisitorCookie` POST succeeds and the server-set
 * copy takes over. Skipped when the cookie already agrees, so the ordinary
 * page load writes nothing here.
 */
function mirrorVisitorCookie(leadId: string): void {
  try {
    if (typeof document === "undefined") return;
    if (readCookie(VISITOR_COOKIE) === leadId) return;
    document.cookie = serializeVisitorCookie(
      VISITOR_COOKIE,
      leadId,
      VISITOR_MAX_AGE_SECONDS,
      typeof location !== "undefined" && location.protocol === "https:",
    );
  } catch {
    // Cookies blocked outright — localStorage still holds the id, and the
    // server-set path retries on the next page load.
  }
}

/**
 * The click id to report a sale against: the same cookie-plus-storage cascade,
 * resolved by RECENCY rather than by tier.
 *
 * Recency, because the rule for a click id is last touch (see `captureLanding`)
 * and the two halves can legitimately disagree about which touch that was — a
 * click captured while cookies were blocked lives only in storage, and one
 * captured before ITP swept storage lives only in the cookie. Preferring a tier
 * would report the older click in one of those directions; preferring the newer
 * `at` is the same rule both halves were written under.
 *
 * Past `MAX_CLICK_AGE_MS` the click is EVICTED, not returned. Every platform's
 * import window has closed by then and the server can no longer tell: it drops
 * the implausible timestamp, falls back to the payment time, uploads a
 * long-dead id, takes the 200, and stamps the sale as reported while Google
 * silently discards it. A click we can no longer report is worse than no click
 * at all, so it does not survive being read.
 */
export function resolveStoredClick(now: number): StoredClickId | null {
  const fromCookie = decodeClickCookie(readCookie(CLICK_COOKIE));
  const fromStorage = getPref(PREF_KEYS.clickId, null);
  const click =
    fromCookie && (!fromStorage || fromCookie.at > fromStorage.at)
      ? fromCookie
      : fromStorage;
  if (!click) return null;

  if (now - click.at > MAX_CLICK_AGE_MS) {
    removePref(PREF_KEYS.clickId);
    // The cookie half is not cleared here: it carries its own expiry, sized to
    // the remainder of the same window (`clickMaxAge`), so it lapses on its own
    // and a `document.cookie` clear could not outlive ITP anyway.
    return null;
  }

  // Mirror a cookie-sourced click back down, so a reader whose storage was
  // swept once does not re-read the cookie on every checkout.
  if (click !== fromStorage) setPref(PREF_KEYS.clickId, click);
  return click;
}

/**
 * Re-issue this reader's attribution ids as SERVER-set cookies, fire and
 * forget.
 *
 * This is the whole durability mechanism: `Set-Cookie` from a first-party
 * response is the one form of storage WebKit does not sweep on the schedule it
 * sweeps `localStorage` and `document.cookie` on. Called once per page load, so
 * the 90-day window is 90 days of INACTIVITY — a reader who visits monthly
 * keeps their click id for the whole upload window and beyond.
 *
 * Contained twice over, because it runs inside `recordLanding`, which sits in a
 * `useState` initializer above the router: a synchronous throw (no `fetch` in
 * some embedded webview, a `TypeError` on a relative URL under a test runtime)
 * is caught, and the rejection every offline page load produces is swallowed
 * rather than left to surface as an unhandled rejection that our own
 * `web.error` handler would then dutifully report. `?.` guards the `.catch`
 * because a stubbed `fetch` need not return a promise.
 */
function syncVisitorCookie(leadId: string, click: StoredClickId | null): void {
  if (!leadId) return;
  const body: Record<string, string | number> = { leadId };
  if (click) {
    body.clickId = click.value;
    body.clickSource = click.source;
    body.clickAt = click.at;
  }
  try {
    void fetch(VISITOR_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      // Same-origin so the response's `Set-Cookie` is honoured, and `keepalive`
      // so a reader who clicks through before it lands still gets the cookie —
      // the landing page load is exactly when they are most likely to leave.
      credentials: "same-origin",
      keepalive: true,
    })?.catch(() => {
      // Fire-and-forget: a failed refresh is retried on the next page
      // load, and there is no one to tell about it in the meantime.
    });
  } catch {
    // The cookie is not refreshed this visit; `localStorage` still holds the
    // ids and the next page load tries again.
  }
}

/**
 * Remove the attribution params from the address bar once captured, so the
 * reader does not copy a campaign-tagged link into a group chat and hand
 * everyone there the same lead id.
 */
function stripCapturedParams(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of [LEAD_PARAM, ...CAPTURED_PARAMS]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return;
  const search = url.searchParams.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${search ? `?${search}` : ""}${url.hash}`,
  );
}

/**
 * Once-per-page-load guard. The capture below writes to `localStorage` and
 * the address bar and emits an event, so it must not run twice — and React's
 * StrictMode invokes a `useState` initializer more than once by design. A
 * module-level memo makes "exactly once per page load" true by construction
 * rather than by hoping about render counts.
 */
let recorded: string | null = null;

/**
 * The capture itself, all of it impure: storage, the address bar, `document`,
 * and one emitted event. Called exactly once per page load, inside the
 * containment in `recordLanding`.
 */
function captureLanding(): void {
  const search = window.location.search;
  const inbound = new URLSearchParams(search).get(LEAD_PARAM);
  const now = Date.now();

  // Memoized the instant it is known, BEFORE anything below that can throw: a
  // `SecurityError` out of the address-bar rewrite must not also cost us the
  // lead id that was already resolved and stored.
  recorded = resolveLeadId(inbound);

  const landing = parseLanding(
    search,
    document.referrer,
    window.location.hostname,
  );
  let landedClick: StoredClickId | null = null;
  if (landing) {
    const click = pickClickId(landing.clickIds, now);
    landedClick = click;
    createClientLogger({ leadId: recorded }).event(
      "acquisition.landed",
      landedPayload(landing, click),
    );

    // LAST TOUCH, and only for the click id. Google Ads (and every other
    // offline-conversion import) credits a conversion to THE CLICK THAT LED TO
    // IT, so the id we must be holding when the reader pays is the most recent
    // one they arrived on — not the first. Overwriting is therefore the
    // correct behaviour here even though the event stream above deliberately
    // keeps every touch: the stream answers "which campaigns touched this
    // reader", this key answers "which click do I report this sale against".
    //
    // A landing with UTM tags but no click id leaves the stored one alone: an
    // organic visit between an ad click and the purchase must not erase the ad
    // click, because there is nothing to report the sale against instead.
    if (click) setPref(PREF_KEYS.clickId, click);
  }

  // Fired HERE rather than inside `resolveLeadId`, and before the address-bar
  // rewrite that is the one known way to throw past this point. Here, because
  // this is where the ids are finally settled: a landing that just captured a
  // new click must persist THAT one, and `resolveLeadId` runs before it exists
  // — firing there would send the previous click and need a second request to
  // correct itself. `resolveStoredClick` covers the ordinary visit that carried
  // no click, which is what refreshes an existing cookie's 90-day window.
  syncVisitorCookie(recorded, landedClick ?? resolveStoredClick(now));

  stripCapturedParams();
}

/**
 * Record this page load's attribution and return the lead id.
 *
 * Emits `acquisition.landed` when the landing carried any campaign signal, and
 * stores the click id (if any) for checkout to send on. Idempotent: later
 * calls return the same id without re-emitting or re-storing.
 *
 * CONTAINED, because this runs in a `useState` initializer above the router:
 * anything that escapes is a blank page for every reader rather than one lost
 * attribution row. Both known ways in are real — `crypto.randomUUID` is
 * undefined in an insecure context (a plain-http LAN preview), and
 * `history.replaceState` throws `SecurityError` in a sandboxed iframe with an
 * opaque origin.
 */
export function recordLanding(): string {
  if (recorded !== null) return recorded;

  if (typeof window === "undefined") {
    // No DOM (SSR, non-browser test runtime): nothing to capture and nothing
    // to persist. Deliberately not memoized, so the first real browser call
    // still does the work.
    return "";
  }

  try {
    captureLanding();
  } catch (error) {
    reportCaptureFailure(error);
  }

  // A capture that threw before it had resolved an id memoizes the empty one,
  // so the failure costs this page load's attribution once and is never
  // retried into a second throw.
  recorded ??= "";
  return recorded;
}

/**
 * Report a capture failure — itself defensively, because the likeliest cause
 * (`crypto.randomUUID` missing in an insecure context) is also what
 * `getSessionId` needs to build the event. An error report that throws would
 * defeat the containment it exists to explain.
 */
function reportCaptureFailure(error: unknown): void {
  try {
    createClientLogger().error("acquisition.capture_failed", error);
  } catch {
    // Nothing left to report with. Attribution is lost; the app is not.
  }
}

/** Reset the once-per-page-load memo. Test seam only. */
export function resetLandingForTest(): void {
  recorded = null;
}
